import type { Task } from '@coachapp/shared';
import { Calendar, Check, Pencil, Plus, Trash2, User as UserIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import ErrorAlert from '../components/ErrorAlert';
import { ScreenHeader } from '../components/layout/ScreenHeader';
import { useTransitionNavigate } from '../hooks/useTransitionNavigate';
import { getApiErrorMessage } from '../lib/api-error';
import { sortTasks } from '../lib/task-sort';
import {
  useCompleteTaskMutation,
  useDeleteTaskMutation,
  useGetTasksQuery,
} from '../store/coachApi';

type StatusFilter = 'ALL' | 'OPEN' | 'DONE';

const TasksPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useTransitionNavigate();
  const [searchParams] = useSearchParams();
  const clientIdParam = searchParams.get('clientId') || undefined;
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');

  const queryArgs = useMemo(() => {
    const args: { status?: string; clientId?: string } = {};
    if (statusFilter !== 'ALL') args.status = statusFilter;
    if (clientIdParam) args.clientId = clientIdParam;
    return Object.keys(args).length > 0 ? args : undefined;
  }, [statusFilter, clientIdParam]);
  const { data: tasks = [], error, isLoading, refetch } = useGetTasksQuery(queryArgs);
  const sortedTasks = useMemo(() => sortTasks(tasks), [tasks]);
  const [completeTask] = useCompleteTaskMutation();
  const [deleteTask] = useDeleteTaskMutation();
  const [completingId, setCompletingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const fetchError = error ? getApiErrorMessage(error, 'Не удалось загрузить задачи') : null;
  const loading = isLoading;

  const handleComplete = async (task: Task) => {
    if (task.status === 'DONE') return;

    setCompletingId(task.id);
    try {
      await completeTask(task.id).unwrap();
    } catch (err) {
      setActionError(getApiErrorMessage(err, 'Ошибка при сохранении'));
    } finally {
      setCompletingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('Вы уверены, что хотите удалить эту задачу?'))) return;

    setDeletingId(id);
    try {
      await deleteTask(id).unwrap();
    } catch (err) {
      setActionError(getApiErrorMessage(err, 'Ошибка при сохранении'));
    } finally {
      setDeletingId(null);
    }
  };

  const openCreate = () => {
    const path = clientIdParam ? `/tasks/new?clientId=${clientIdParam}` : '/tasks/new';
    navigate(path);
  };

  const openEdit = (task: Task) => {
    navigate(`/tasks/${task.id}/edit`, { state: { task } });
  };

  const formatDueDate = (dueAt?: string) => {
    if (!dueAt) return null;
    return new Date(dueAt).toLocaleString(i18n.language === 'ru' ? 'ru-RU' : 'en-US', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filters: { key: StatusFilter; label: string }[] = [
    { key: 'ALL', label: t('Все') },
    { key: 'OPEN', label: t('Открытые') },
    { key: 'DONE', label: t('Выполненные') },
  ];

  return (
    <>
      <div className="page-container">
        <ScreenHeader
          title={t('Задания')}
          subtitle={
            tasks.length > 0
              ? t('У вас {{count}} задач', { count: sortedTasks.length })
              : t('Нет задач')
          }
          onBack={() => navigate('/profile')}
          backLabel={t('Назад')}
          actions={
            <button type="button" className="btn-primary w-full md:w-auto shrink-0" onClick={openCreate}>
              <Plus size={18} />
              <span>{t('Новая задача')}</span>
            </button>
          }
        />

        {fetchError && (
          <ErrorAlert
            message={fetchError}
            onRetry={() => void refetch()}
            onDismiss={() => {}}
          />
        )}
        {actionError && (
          <ErrorAlert message={actionError} onDismiss={() => setActionError(null)} />
        )}

        <div className="flex gap-2 mb-6 overflow-x-auto pb-1 -mx-1 px-1 md:overflow-visible">
          {filters.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setStatusFilter(f.key)}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors shrink-0 ${
                statusFilter === f.key
                  ? 'bg-primary text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="spinner h-8 w-8" />
          </div>
        ) : sortedTasks.length === 0 ? (
          <div className="empty-state p-12">
            <p className="font-medium">{t('Нет задач')}</p>
            <p className="text-sm mt-1 text-slate-500">{t('Создайте первую задачу для ежедневной работы')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {sortedTasks.map((task) => {
              const dueLabel = formatDueDate(task.dueAt);
              const isDone = task.status === 'DONE';

              return (
                <div
                  key={task.id}
                  className={`card p-4 md:p-5 flex items-start gap-3 ${isDone ? 'opacity-60' : ''}`}
                >
                  <button
                    type="button"
                    onClick={() => handleComplete(task)}
                    disabled={isDone || completingId === task.id}
                    className={`mt-0.5 w-6 h-6 rounded-lg border-2 flex items-center justify-center shrink-0 transition-colors ${
                      isDone
                        ? 'bg-emerald-500 border-emerald-500 text-white'
                        : 'border-slate-300 hover:border-primary'
                    }`}
                    aria-label={t('Отметить выполненной')}
                  >
                    {completingId === task.id ? (
                      <div className="w-3 h-3 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    ) : isDone ? (
                      <Check size={14} />
                    ) : null}
                  </button>

                  <div className="flex-1 min-w-0">
                    <p className={`font-semibold text-slate-900 ${isDone ? 'line-through' : ''}`}>
                      {task.title}
                    </p>
                    {task.description && (
                      <p className="text-sm text-slate-500 mt-1 line-clamp-2">{task.description}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-slate-500">
                      {task.client?.name && (
                        <span className="flex items-center gap-1">
                          <UserIcon size={12} />
                          {task.client.name}
                        </span>
                      )}
                      {dueLabel && (
                        <span className="flex items-center gap-1">
                          <Calendar size={12} />
                          {dueLabel}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => openEdit(task)}
                      className="p-2 hover:bg-slate-100 rounded-xl transition-colors text-slate-400 hover:text-primary"
                      title={t('Редактировать')}
                    >
                      <Pencil size={18} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(task.id)}
                      disabled={deletingId === task.id}
                      className="p-2 hover:bg-red-50 rounded-xl transition-colors text-slate-400 hover:text-red-500 disabled:opacity-50"
                      title={t('Удалить')}
                    >
                      {deletingId === task.id ? (
                        <div className="w-4 h-4 animate-spin rounded-full border-2 border-red-500 border-t-transparent" />
                      ) : (
                        <Trash2 size={18} />
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default TasksPage;
